import axios, { AxiosInstance, InternalAxiosRequestConfig } from 'axios'

// API портала живёт на api.verrafied.ai / dev.api.verrafied.ai, а не на хосте портала
const DEALER_API_BASE_URL =
  (import.meta.env.VITE_API_BASE_URL?.replace('/api/v1', '') || 'https://api.verrafied.ai') +
  '/api/v1/dealer'

const ACCESS_TOKEN_KEY = 'dealer_access_token'
const REFRESH_TOKEN_KEY = 'dealer_refresh_token'

// публичные страницы портала: с них не уводим на логин при протухшей сессии
export const PUBLIC_PATHS = ['/login', '/forgot-password', '/reset-password', '/set-password']

// запросы, для которых 401 — это ответ по существу, а не протухший access-токен
const NO_REFRESH_URLS = ['/auth/login', '/auth/refresh', '/auth/forgot-password', '/auth/reset-password']

// «Remember me»: токены в localStorage (вход живёт до 30 дней, пока жив refresh-токен);
// без галочки — в sessionStorage вкладки, вход сбрасывается при закрытии браузера (общий компьютер на стойке)
export const tokenStorage = {
  getAccess: () => sessionStorage.getItem(ACCESS_TOKEN_KEY) ?? localStorage.getItem(ACCESS_TOKEN_KEY),
  getRefresh: () => sessionStorage.getItem(REFRESH_TOKEN_KEY) ?? localStorage.getItem(REFRESH_TOKEN_KEY),
  // remember не передан (обновление токена) — сохраняем туда же, где лежит текущая сессия
  set: (access: string, refresh: string, remember?: boolean) => {
    const persist = remember ?? sessionStorage.getItem(REFRESH_TOKEN_KEY) === null
    tokenStorage.clear()
    const store = persist ? localStorage : sessionStorage
    store.setItem(ACCESS_TOKEN_KEY, access)
    store.setItem(REFRESH_TOKEN_KEY, refresh)
  },
  clear: () => {
    for (const store of [localStorage, sessionStorage]) {
      store.removeItem(ACCESS_TOKEN_KEY)
      store.removeItem(REFRESH_TOKEN_KEY)
    }
  },
}

function redirectToLogin(reason: string) {
  tokenStorage.clear()
  if (PUBLIC_PATHS.includes(window.location.pathname)) return
  window.location.href = `/login?reason=${reason}`
}

const dealerApiClient: AxiosInstance = axios.create({
  baseURL: DEALER_API_BASE_URL,
  timeout: 15000,
  headers: { 'Content-Type': 'application/json' },
})

dealerApiClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = tokenStorage.getAccess()
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

let isRefreshing = false
let refreshQueue: Array<{ resolve: (token: string) => void; reject: (err: unknown) => void }> = []

dealerApiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config
    const status = error.response?.status
    const code = error.response?.data?.code ?? error.response?.data?.errorCode

    // ДЦ заблокировали посреди сессии — дальше работать нельзя
    if (status === 403 && code === 'BRANCH_SUSPENDED' && !NO_REFRESH_URLS.includes(original?.url)) {
      redirectToLogin('branch_suspended')
      return Promise.reject(error)
    }

    if (status !== 401 || !original || original._retry || NO_REFRESH_URLS.includes(original.url)) {
      return Promise.reject(error)
    }

    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        refreshQueue.push({
          resolve: (token) => {
            original.headers.Authorization = `Bearer ${token}`
            resolve(dealerApiClient(original))
          },
          reject,
        })
      })
    }

    original._retry = true
    isRefreshing = true
    const refreshToken = tokenStorage.getRefresh()

    try {
      if (!refreshToken) {
        redirectToLogin('session_expired')
        return Promise.reject(error)
      }

      // бэк, как и в админке, принимает refreshToken query-параметром
      const { data } = await axios.post(`${DEALER_API_BASE_URL}/auth/refresh`, null, { params: { refreshToken } })

      tokenStorage.set(data.accessToken, data.refreshToken)
      refreshQueue.forEach((entry) => entry.resolve(data.accessToken))
      refreshQueue = []

      original.headers.Authorization = `Bearer ${data.accessToken}`
      return dealerApiClient(original)
    } catch (refreshError) {
      // другая вкладка уже обновила токены (старый refresh-токен на бэке удаляется при обновлении) —
      // продолжаем с её новыми токенами, а не выкидываем на логин
      const latestRefresh = tokenStorage.getRefresh()
      const latestAccess = tokenStorage.getAccess()
      if (latestRefresh && latestAccess && latestRefresh !== refreshToken) {
        refreshQueue.forEach((entry) => entry.resolve(latestAccess))
        refreshQueue = []
        original.headers.Authorization = `Bearer ${latestAccess}`
        return dealerApiClient(original)
      }
      refreshQueue.forEach((entry) => entry.reject(refreshError))
      refreshQueue = []
      redirectToLogin('session_expired')
      return Promise.reject(refreshError)
    } finally {
      isRefreshing = false
    }
  }
)

export default dealerApiClient
